import React, { useState } from 'react'
import type { Entry } from 'contentful'
import RichText from '@/components/ui/rich-text'
import DynamicAction, { type DynamicActionProps } from '@/components/ui/dynamic-action'

import { buttonStyles, type VariantType } from '@/components/ui/cta-link'

import { cx } from '@/utils/strings'
import useMutationObserver from '@/utils/useMutationObserver'
import type {
  TypeComponentCtaLinkSkeleton,
  TypeComponentHubspotCtaSkeleton,
  TypeSectionCtaSkeleton,
  TypeComponentLottieSkeleton,
} from '@/types/ctf'
import { useEventListener } from 'usehooks-ts'

import Lottie from '../lottie'
import Image from '@/components/ui/image'
import GradientBackgroundWrapper from '@/components/ui/gradient-background-wrapper'

type LottieEntry = Entry<TypeComponentLottieSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

type SectionCtaEntry = Entry<TypeSectionCtaSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type ComponentCtaLinkEntry = Entry<TypeComponentCtaLinkSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>
type ComponentHubspotCtaEntry = Entry<TypeComponentHubspotCtaSkeleton, 'WITHOUT_UNRESOLVABLE_LINKS'>

type SectionCtaProps = SectionCtaEntry['fields'] & {
  className?: string
}

export default function Cta({
  actions,
  backgroundImage,
  body,
  className,
  eyebrowText,
  headline,
  image,
  sticky,
  textAlign = 'Left',
  asset,
}: SectionCtaProps) {
  const [headerHeight, setHeaderHeight] = useState(0)
  const calcHeaderHeight = () => {
    const header = document.querySelector('header')
    if (header) {
      const height = header.getBoundingClientRect().bottom
      setHeaderHeight(height)
    }
  }
  useMutationObserver('header', calcHeaderHeight)
  useEventListener('resize', calcHeaderHeight)

  const renderCTA = (
    action: ComponentCtaLinkEntry | ComponentHubspotCtaEntry | undefined,
    btnStyle: VariantType,
    scrollToOffset = 0
  ) => {
    if (!action) return null
    const type = action.sys.contentType.sys.id
    const fields = {
      type,
      ...action.fields,
    } as DynamicActionProps

    return (
      <DynamicAction
        key={action.sys.id}
        {...fields}
        style={sticky ? btnStyle : fields.style}
        scrollToOffset={scrollToOffset}
        id={action.sys.id}
      />
    )
  }

  // Content wrapper that only renders div when there's an image or asset
  const ContentWrapper = ({ children }: { children: React.ReactNode }) => {
    if (image || asset) {
      return <div className="md:w-2/3">{children}</div>
    }
    return <>{children}</>
  }

  return sticky ? (
    <section
      id="sticky-cta"
      className={cx('max-w-8xl mx-auto sm:sticky z-20 px-0 lg:px-9 w-fit', className)}
      style={headerHeight ? { top: `${headerHeight + 12}px` } : undefined}
    >
      {actions && (
        <div className="bg-white sm:bg-transparent">
          <div
            className={cx(
              'flex flex-col shrink-0 flex-wrap gap-0 sm:gap-4 sm:flex-row sm:rounded-full p-3 sm:shadow-even-around bg-white dark:bg-secondary-800',
              (textAlign === 'Center' || !textAlign) && 'items-center',
              textAlign === 'Left' && 'items-start',
              textAlign === 'Right' && 'items-end'
            )}
            data-testid="actions"
          >
            {actions.map((action: any, idx) => (
              <div key={idx}>
                <div className="block sm:hidden">{renderCTA(action, buttonStyles.btnArrow)}</div>
                <div className={cx(sticky ? `hidden sm:flex` : `flex`, `items-center relative`)}>
                  {renderCTA(action, buttonStyles.btnSolid, 55)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  ) : (
    <GradientBackgroundWrapper
      containerClassName={className}
      backgroundStyle="Gradient 2"
      innerClassName={cx(
        'p-6 lg:p-10',
        (image || asset) && 'flex gap-8 flex-col-reverse md:flex-row items-center',
        (textAlign === 'Center' || !textAlign) && 'text-center',
        textAlign === 'Left' && 'text-left',
        textAlign === 'Right' && 'text-right'
      )}
    >
      {(body || eyebrowText || headline || actions) && (
        <>
          <ContentWrapper>
            {eyebrowText && <p className="text-eyebrow uppercase mb-3">{eyebrowText}</p>}
            {headline && <h4 className="h4 text-balance">{headline}</h4>}
            {body && (
              <div className="[&_p]:[text-wrap:balance] mb-6">
                <RichText body={body} />
              </div>
            )}
            {actions && (
              <div
                className={cx(
                  !body && headline && 'mt-6',
                  !body && !headline && 'mt-0',
                  'flex shrink-0 flex-wrap gap-5 flex-row w-full items-center',
                  (textAlign === 'Center' || !textAlign) && 'items-center justify-center'
                )}
                data-testid="actions"
              >
                {actions.map((action: any) => (
                  <div key={action.sys.id} className="w-full sm:w-auto">
                    <div className="items-center relative dark:after:bg-secondary-500">
                      {renderCTA(action, buttonStyles.btnSolid, 50)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ContentWrapper>
          {image && (
            <div className="md:w-1/3 [&_svg]:fill-none [&_figure]:m-0">
              <Image image={image} className="rounded" />
            </div>
          )}
          {asset && !image && (
            <>
              {asset?.sys?.contentType?.sys?.id === 'componentLottie' && (
                <div className="md:w-1/3 [&_svg]:fill-none [&_figure]:m-0">
                  <Lottie {...(asset as LottieEntry)?.fields} />
                </div>
              )}
              {asset?.sys?.contentType?.sys?.id === 'resourceImage' && (
                <div className="md:w-1/3 [&_svg]:fill-none [&_figure]:m-0 [&_svg]:w-full [&_svg]:h-auto">
                  <Image {...(asset?.fields as any)} darkModeImage={asset.imageDarkMode?.fields} />
                </div>
              )}
            </>
          )}
        </>
      )}

      {backgroundImage && (
        <div
          className="rounded-lg absolute left-0 right-0 top-0 bottom-0 w-full h-full z-0 bg-cover bg-center bg-no-repeat "
          style={backgroundImage ? { backgroundImage: `url(${backgroundImage.fields.file?.url})` } : undefined}
        />
      )}
    </GradientBackgroundWrapper>
  )
}
